/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@/shared/ui';

interface LaunchInlineConfirmProps {
  title: string;
  description: string;
  confirmLabel: string;
  destructive?: boolean;
  /** When set, the action unlocks only once the word is typed back. */
  confirmWord?: string;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Confirmation rendered inside the unit sheet, not over it.
 *
 * On the phone a centred dialog opened a second modal on top of the sheet,
 * leaving two stacked surfaces and an inert board behind them — and for
 * the typed confirmation the keyboard covered the buttons, which is the
 * one place that must stay reachable. Here the confirmation takes over the
 * sheet it belongs to, and its buttons sit at the bottom of that sheet.
 */
export function LaunchInlineConfirm({
  title,
  description,
  confirmLabel,
  destructive = true,
  confirmWord,
  onCancel,
  onConfirm,
}: LaunchInlineConfirmProps) {
  const { t } = useTranslation('features/launches');
  const [typed, setTyped] = useState('');
  const unlocked = !confirmWord || typed.trim().toUpperCase() === confirmWord;

  return (
    <div
      data-testid="launch-inline-confirm"
      className="flex flex-col gap-[var(--space-4)]"
    >
      <div className="flex flex-col gap-[var(--space-2)]">
        <Text
          as="span"
          className="text-[length:var(--text-app-section)] [font-weight:var(--font-semibold)] text-[var(--text-primary)]"
        >
          {title}
        </Text>
        <Text as="span" className="text-[length:var(--text-body-sm)] text-[var(--text-secondary)]">
          {description}
        </Text>
      </div>

      {confirmWord && (
        <label className="flex flex-col gap-[var(--space-2)]">
          <Text as="span" className="text-[length:var(--text-meta)] text-[var(--text-tertiary)]">
            {t('confirm.typeToConfirm', { word: confirmWord })}
          </Text>
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoCapitalize="characters"
            className="min-h-[var(--space-12)] rounded-(--radius-md) border border-[var(--border-medium)] bg-[var(--surface-primary)] px-[var(--space-3)] text-[length:var(--text-body)] text-[var(--text-primary)]"
          />
        </label>
      )}

      {/* Stacked, full width, at the bottom of the sheet: with the keyboard
          up there is no room for a side-by-side row, and the confirm must
          never be the button that falls off screen. */}
      <div className="flex flex-col gap-[var(--space-2)]">
        <button
          type="button"
          disabled={!unlocked}
          onClick={onConfirm}
          className="flex min-h-[var(--space-12)] w-full items-center justify-center rounded-(--radius-md) text-[length:var(--text-body-sm)] [font-weight:var(--font-medium)]"
          style={{
            background: destructive ? 'var(--launch-danger-fg)' : 'var(--seed-base)',
            color: 'var(--text-inverse)',
            opacity: unlocked ? undefined : 'var(--launch-opacity-disabled)',
          }}
        >
          {confirmLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex min-h-[var(--space-12)] w-full items-center justify-center rounded-(--radius-md) border border-[var(--border-subtle)] text-[length:var(--text-body-sm)] text-[var(--text-secondary)]"
        >
          {t('confirm.cancel')}
        </button>
      </div>
    </div>
  );
}
